import {
  AHEmptyScreen,
  AHSeparator,
  AHText,
  Box,
  ColorType,
  ListItem,
  TouchableBox,
  AHIcon,
} from "arkh";
import {
  useLazyLoadQuery,
  graphql,
  usePaginationFragment,
  useFragment,
} from "react-relay";

import {
  ActivityIndicator,
  FlatList,
  StyleProp,
  ViewStyle,
} from "react-native";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import React, {
  Suspense,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useOrgHomeDetailLayout } from "../useOrgHomeDetailContainerStyle";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  FileConnectionSortDirectionField,
  FileConnectionSortField,
  WorkspaceFilesQuery,
} from "./__generated__/WorkspaceFilesQuery.graphql";
import { WorkspaceFilesList_Workspace$key } from "./__generated__/WorkspaceFilesList_Workspace.graphql";
import { WorkspaceFilesFile_file$key } from "./__generated__/WorkspaceFilesFile_file.graphql";
import { FileIconBox } from "../../Files/FileListItem/FileIcon";
import {
  PendingFile,
  WorkspaceFileUploadArea,
  getDocumentsFromPicker,
} from "../../Files/FileUploadArea";

import { FileRowWrapper } from "./FileRowWrapper";
import { FileListItemRow } from "./FileListItemRow";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { FormattedMessage, useIntl } from "react-intl";

import { DropFileZone } from "../../Files/DropFileZone";
import { listGutterStyle } from "../guterStyle";
import { OrgButton } from "../OrgButton";
import { SortableColumnTitle } from "../SortableColumnTitle";
import { LoadingList } from "./LoadingList";
import { useWorkspaceId, useWorkspacePermissions } from "../workspaceId";
import { FormattedDateInWorkspace } from "../../../common/dates/FormattedDate";
import { Image } from "expo-image";
import { MAX_FILE_SIZE } from "../../Files/fileUtils/fileConstants";
import { WorkspaceDetailNavigationHeader } from "../WorkspaceDetailNavigationHeader";
import { FileType } from "../../Files/__generated__/FileInfoScreenQuery.graphql";

function FileSubtitle() {
  return (
    <FormattedMessage
      description="Org Workspace Files description"
      defaultMessage="Upload files here to make them available to one, multiple, or all units within the current workspace."
    />
  );
}
function InnerWorkspaceFilesScreen(
  props: OrgHomeScreenProps<"WorkspaceFiles"> & { fetchKey: string }
) {
  const onOpenFile = useCallback(
    (file: { id: string }) => {
      props.navigation.navigateDeprecated("File", {
        id: file.id,
      });
    },
    [props.navigation]
  );

  const wId = useWorkspaceId();
  const permissions = useWorkspacePermissions();
  return (
    <WorkspaceFilesScreenComponent
      onFileItemPress={onOpenFile}
      fetchKey={props.fetchKey}
      subTitle={<FileSubtitle />}
      workspaceId={wId}
      write={permissions.write}
      headerType="screen"
    />
  );
}

export function WorkspaceFilesScreenComponent({
  onFileItemPress,
  fetchKey,
  itemActionComponent,
  onUploadedFiles: _onUploadedFiles,
  subTitle,
  workspaceId,
  initialFiles,
  color = "surface-background-light",
  headerType = "none",
  write,
}: {
  onFileItemPress: (file: {
    id: string;
    name: string;
    type: FileType | null;
    url: string;
  }) => void;
  fetchKey: string;
  itemActionComponent?: React.ReactNode;
  onUploadedFiles?: (fileIds: []) => void;
  headerType?: "none" | "screen";
  subTitle?: React.ReactNode;
  initialFiles?: PendingFile[];
  workspaceId: string;
  color?: ColorType;
  write: boolean;
}) {
  const [fk, setFk] = useState(0);
  const [orderBy, setOrderBy] = useState<FileConnectionSortField>("NAME");
  const [order, setOrder] = useState<FileConnectionSortDirectionField>("DESC");
  const data = useLazyLoadQuery<WorkspaceFilesQuery>(
    graphql`
      query WorkspaceFilesQuery(
        $first: Int
        $after: String
        $orderBy: FileConnectionSortField
        $order: FileConnectionSortDirectionField
        $workspaceId: ID!
      ) {
        workspace(id: $workspaceId) {
          ...FileUploadAreaWorkspace_workspace
          ...WorkspaceFilesList_Workspace
        }
      }
    `,
    {
      orderBy,
      order,
      workspaceId,
    },
    {
      fetchKey: fetchKey + fk,
      fetchPolicy: "store-and-network",
    }
  );
  const refresh = useCallback(() => {
    setFk((prev) => prev + 1);
  }, []);

  const onUploadedFiles = useCallback(
    (fileIds: []) => {
      refresh();
      setOrderBy("CREATED_AT");
      setOrder("DESC");
    },
    [refresh]
  );

  const [filesToBeUploaded, setFiles] = useState<PendingFile[]>(
    initialFiles ?? []
  );

  const onRequestUpload = useCallback(() => {
    getDocumentsFromPicker().then((res) => {
      if (res?.hasError) {
        // TODO: Show error
      }
      if (res) {
        setFiles(res.newFiles);
      }
    });
  }, []);

  const intl_tokens = useIntlTokens();

  const header = useMemo(
    () => (
      <>
        {headerType === "none" && write ? (
          <Box
            color="surface-background-light"
            style={{
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
            spaceTop="medium"
          >
            <OrgButton
              backgroundColor="primary"
              icon="add"
              tintColor="text-inverted"
              title={intl_tokens.Upload}
              onPress={onRequestUpload}
            />
          </Box>
        ) : null}
        <Box color={color} spaceTop="medium">
          <FileListItemHeader
            onRefresh={refresh}
            order={order}
            onOrderChange={setOrder}
            onOrderByChange={setOrderBy}
            orderBy={orderBy}
          />
        </Box>
      </>
    ),
    [
      headerType,
      write,
      intl_tokens.Upload,
      onRequestUpload,
      color,
      refresh,
      order,
      orderBy,
    ]
  );

  const detailLayout = useOrgHomeDetailLayout();
  return (
    <DropFileZone
      type="FILE"
      maxSize={MAX_FILE_SIZE}
      onDropFilesNew={(f) => {
        setFiles(f);
      }}
    >
      {headerType === "screen" ? (
        <WorkspaceDetailNavigationHeader
          left={<AHText type="subtitle">{intl_tokens.Files}</AHText>}
          right={
            write ? (
              <OrgButton
                backgroundColor="primary"
                icon="add"
                tintColor="text-inverted"
                title={intl_tokens.Upload}
                onPress={onRequestUpload}
              />
            ) : null
          }
        />
      ) : null}
      <Suspense fallback={<LoadingList header={header} />}>
        <WorkspaceFilesList
          contentContainerStyle={detailLayout.contentContainerStyle}
          onRequestUpload={onRequestUpload}
          list={data.workspace}
          onFileItemPress={onFileItemPress}
          header={header}
          itemActionComponent={itemActionComponent}
          subtitle={subTitle}
          write={write}
        />
      </Suspense>
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fk) => {
          return (
            <WorkspaceFileUploadArea
              workspace={data.workspace}
              fetchKey={fk + filesToBeUploaded.map((f) => f.id).join("")}
              files={filesToBeUploaded}
              onDropedFiles={(dropedFiles) =>
                setFiles((prev) => [...prev, ...dropedFiles])
              }
              onRemoveFile={(id) => {
                setFiles(filesToBeUploaded.filter((f) => f.id !== id));
              }}
              onCancel={() => {
                setFiles([]);
              }}
              onUploadedFiles={_onUploadedFiles ?? onUploadedFiles}
            />
          );
        }}
      </LazyQueryNetworkBoundrary>
    </DropFileZone>
  );
}

function WorkspaceFilesList(props: {
  list: WorkspaceFilesList_Workspace$key;
  onFileItemPress: (file: {
    id: string;
    name: string;
    type: FileType | null;
    url: string;
  }) => void;
  subtitle: React.ReactNode;
  header: React.ReactElement;
  itemActionComponent?: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  onRequestUpload: () => void;
  write: boolean;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceFilesList_Workspace on Workspace
      @refetchable(queryName: "WorkspaceFilesList_WorkspaceRefetchQuery") {
        id
        files(first: $first, after: $after, orderBy: $orderBy, order: $order)
          @connection(key: "WorkspaceFilesList__files") {
          edges {
            node {
              id
              ...WorkspaceFilesFile_file
            }
          }
        }
      }
    `,
    props.list
  );

  const listData = useMemo(() => {
    return data.files.edges.filter((e) => e.node?.id);
  }, [data.files.edges]);
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <FileListItem
          file={item.node}
          onPress={props.onFileItemPress}
          isLast={index + 1 === listData.length}
        />
      );
    },
    [listData.length, props.onFileItemPress]
  );

  const detailLayout = useOrgHomeDetailLayout();

  const intl_tokens = useIntlTokens();
  const onReachEnd = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(30);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  if (data.files.edges.filter((e) => e.node).length === 0) {
    return (
      <AHEmptyScreen
        title={intl_tokens.Files}
        subtitle={props.subtitle}
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./Files.png")}
            />
          </Box>
        }
      />
    );
  }
  return (
    <FlatList
      style={[
        { flex: 1, marginBottom: detailLayout.keyboardBottomInset },
        listGutterStyle,
      ]}
      contentContainerStyle={props.contentContainerStyle}
      data={listData}
      keyboardShouldPersistTaps="handled"
      renderItem={renderItem}
      onEndReached={onReachEnd}
      keyExtractor={(item) => item.node.id}
      ListHeaderComponent={listData.length === 0 ? null : props.header}
      ListFooterComponent={
        hasNext && isLoadingNext ? (
          <Box spaceTop="medium">
            <ActivityIndicator />
          </Box>
        ) : null
      }
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
    />
  );
}

export function WorkspaceFilesScreen(
  props: OrgHomeScreenProps<"WorkspaceFiles">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerWorkspaceFilesScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function FileListItemHeader(props: {
  order: FileConnectionSortDirectionField;
  orderBy: FileConnectionSortField;
  onOrderChange: (order: FileConnectionSortDirectionField) => void;
  onOrderByChange: (orderBy: FileConnectionSortField) => void;
  onRefresh: () => void;
}) {
  const [, startTransition] = useTransition();
  const renderHeaderItem = useCallback(
    (title: string, type: FileConnectionSortField, color: ColorType) => {
      return (
        <SortableColumnTitle
          title={title}
          onOrderChange={(d) => {
            startTransition(() => {
              props.onOrderChange(d);
            });
          }}
          onOrderBySelect={() => {
            startTransition(() => {
              props.onOrderByChange(type);
            });
          }}
          order={type === props.orderBy ? props.order : null}
        />
      );
    },
    [props]
  );

  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: "File name",
    description: "Workspace Files: Filename column title",
  });

  const size = intl.formatMessage({
    defaultMessage: "File size",
    description: "Workspace Files: Size column title",
  });

  const createdAt = intl.formatMessage({
    defaultMessage: "Uploaded",
    description: "Workspace Files: Uploaded column title",
  });

  return (
    <>
      <ListItem isFirst disabled border="border-primary">
        <FileRowWrapper
          icon={null}
          title={renderHeaderItem(title, "NAME", "darkerText")}
          size={renderHeaderItem(size, "SIZE", "darkText")}
          detail={renderHeaderItem(createdAt, "CREATED_AT", "darkText")}
          action={
            <TouchableBox onPress={props.onRefresh}>
              <AHIcon type="update" forceSize={16} color="darkText" />
            </TouchableBox>
          }
        />
      </ListItem>
      <AHSeparator />
    </>
  );
}

function FileListItem(props: {
  file: WorkspaceFilesFile_file$key;
  onPress: (file: {
    id: string;
    name: string;
    type: FileType | null;
    url: string;
  }) => void;

  isLast: boolean;
}) {
  const file = useFragment(
    graphql`
      fragment WorkspaceFilesFile_file on File {
        id
        name
        url
        type
        formattedSize
        createdAtDate {
          ...FormattedDate
        }
        ...FileIconBox_file
      }
    `,
    props.file
  );

  return (
    <ListItem
      onPress={() => {
        props.onPress({
          id: file.id,
          name: file.name ?? "",
          type: file.type,
          url: file.url,
        });
      }}
      isLast={props.isLast}
      border="border-primary"
      displayMode="TABLE"
    >
      <FileListItemRow
        icon={<FileIconBox resolveOrgToFileType file={file} />}
        title={file.name ?? ""}
        size={file.formattedSize}
        detail={<FormattedDateInWorkspace date={file.createdAtDate} />}
      />
    </ListItem>
  );
}
